import React, { useMemo, useRef } from 'react';
import { Flex, Link } from '@chakra-ui/react';
import { TwitterIcon } from '../../utils/icons/twitter';
import { DiscordIcon } from '../../utils/icons/discord';
import { FacebookIcon } from '../../utils/icons/facebook';
import { UrlIcon } from '../../utils/icons/url';
import { useHover } from 'usehooks-ts';
import { contrastColors } from '../../utils/parser';

interface IIcon {
	icon: JSX.Element;
	href: string;
	isWide?: boolean;
	color?: string;
}

/**
 * @param icon
 * @param href
 * @param isWide
 * @param color in hex
 */
export const Icon = ({ icon, href, isWide = false, color = '#ffffff' }: IIcon) => {
	const hoveredItem = useRef(null!);
	const isHovered = useHover(hoveredItem);

	const iconSymbolColor = useMemo(
		() => (!isHovered ? color : contrastColors(color, '#ffffff') > 3.1 ? 'white' : 'black'),
		[color, isHovered]
	);

	return (
		<Link
			href={href}
			target="_blank"
			ref={hoveredItem}
			display="flex"
			borderRadius={isWide ? '99px' : '50%'}
		>
			<Flex
				alignItems="center"
				justify="center"
				bg="transparent"
				borderRadius={isWide ? '99px' : '50%'}
				border={`1px solid ${color}`}
				boxSize="24px"
				width={isWide ? 'auto' : '24px'}
				paddingX={isWide ? '8px' : '0'}
				cursor="pointer"
				_hover={{ backgroundColor: color }}
			>
				{React.cloneElement(icon, { color: iconSymbolColor })}
			</Flex>
		</Link>
	);
};

interface ISocialsPane {
	socials: {
		twitter?: string;
		discord?: string;
		fb?: string;
		gamePaper?: string;
	};
	web?: string;
	colors?: string;
}

export const SocialsPane = ({ socials, web, colors = 'white' }: ISocialsPane) => {
	return (
		<Flex
			gridGap="12px"
			justifyContent="flex-start"
			alignItems="center"
		>
			{socials.twitter && (
				<Icon
					icon={<TwitterIcon color={colors} />}
					href={socials.twitter}
					color={colors}
				/>
			)}
			{socials.discord && (
				<Icon
					icon={<DiscordIcon color={colors} />}
					href={socials.discord}
					color={colors}
				/>
			)}
			{socials.fb && (
				<Icon
					icon={<FacebookIcon color={colors} />}
					href={socials.fb}
					color={colors}
				/>
			)}
			{web && (
				<Icon
					isWide
					icon={<UrlIcon color={colors} />}
					href={web}
					color={colors}
				/>
			)}
		</Flex>
	);
};
